import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Box,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { AddCircleOutline } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { usePayrollCycleRun } from "../../PayrollCycleRunContext";
import { themes } from "services/constants";
import { stickyHeader } from "services/constants";
import { Loader } from "components/Loader";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { onlyNumberRegex } from "services/constants/patterns";
import * as Mui from "@mui/material";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { customGetAPI } from "services/utils/CustomAPI";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";

// Sub-components

const DataTable = ({
  data,
  columns,
  emptyMessage = "No data available",
  renderRow,
  summaryRow,
  emptyAction,
}) => (
  <TableContainer sx={{ mt: 1, backgroundColor: themes?.whiteColor }}>
    <Table size="small" className="border">
      <TableHead sx={stickyHeader}>
        <TableRow>
          {columns.map((column, index) => (
            <TableCell key={index}>{column}</TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {!data || data.length === 0 ? (
          <>
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Alert severity="info">{emptyMessage}</Alert>
              </TableCell>
            </TableRow>

            {emptyAction && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="left"
                  sx={{ cursor: "pointer" }}
                >
                  {emptyAction()}
                </TableCell>
              </TableRow>
            )}
          </>
        ) : (
          <>
            {data.map((item, index) => renderRow(item, index))}
            {summaryRow && summaryRow()}
          </>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const SectionHeader = ({ children }) => (
  <Typography
    sx={{
      fontSize: 16,
      fontWeight: "bold",
      color: "#556774",

      mt: 2,
    }}
  >
    {children}
  </Typography>
);

const getComponentList = (responseDetails) => {
  if (Array.isArray(responseDetails)) return responseDetails;
  if (Array.isArray(responseDetails?.data)) return responseDetails.data;
  if (Array.isArray(responseDetails?.results)) return responseDetails.results;
  return [];
};

const isManualAmountComponent = (component) =>
  ["Fixed Amount", "Calculated"].includes(component?.computation_method);

// Main Component
export const PaymentDetails = ({
  setRecallEmployee,
  attendanceDetails,
  loading,
  recallEarningSummary,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain } = useSelector((state) => state.tokenReducer);
  const [showRemoveDialogBox, setShowRemoveDialogBox] = useState(false);
  const [otherDeduction, setOtherDeduction] = useState(false);
  const { userId, payScheduleId, getCompanyId, payScheduleData } =
    usePayrollCycleRun();
  const {
    watch,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      component_name: "",
      reason: "",
      amount: "",
    },
  });

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [triggerApi, setTriggerApi] = useState(false);
  const [componentData, setComponentData] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [updatedData, setUpdatedData] = useState([]);
  const [deleteOtherEarning, setDeleteOtherEarning] = useState([]);
  const isEditable =
    payScheduleData?.status === "Submitted" ||
    payScheduleData?.status === "Approved" ||
    payScheduleData?.status === "Ready for Review";

  useEffect(() => {
    const fetchIndividualUserDetails = async () => {
      const res = await customGetAPI(
        `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=${encodeURIComponent(
          selectedTable,
        )}&company_id=${getCompanyId}`,
      );
      if (res.ok) {
        const allComponents = getComponentList(res?.responseDetails);

        const manualAmountComponents = allComponents.filter(
          isManualAmountComponent,
        );

        setComponentData(manualAmountComponents);
        setTriggerApi(false);
      } else {
        setTriggerApi(false);
      }
    };
    if (triggerApi) {
      fetchIndividualUserDetails();
    }
  }, [domain, getCompanyId, selectedTable, triggerApi]);

  if (attendanceDetails?.status === "Draft") {
    return (
      <Alert severity="info" fullWidth>
        Payroll run has not been initiated to the user yet.
      </Alert>
    );
  }

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
    setUpdatedData([]);
    reset();
  };

  const hanldeComponetName = (event, data) => {
    if (data) {
      setValue("component_name", data?.name);
      setValue("component_id", data?.id);
      setValue("amount", data?.value);
    } else {
      setValue("component_name", "");
    }
  };

  const hanldeAmount = (event) => {
    if (onlyNumberRegex.test(event.target.value)) {
      setValue("amount", event.target.value);
    } else {
      setValue("amount", "");
    }
  };

  const handleReason = (event) => {
    const reason = event.target.value;
    if (reason) {
      setValue("reason", event.target.value);
    } else {
      setValue("reason", "");
    }
  };

  const updateData = async (data) => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/addother_earnings/?company_id=${requestCompanyId}`,
      "POST",
      {
        pay_schedule_id: payScheduleId,
        user_id: userId,
        amount: data?.amount,
        component_type: selectedTable,
        basis: attendanceDetails?.calculation_based_on,
        component_name: data?.component_name,
      },
    );
    if (response.ok) {
      setComponentData([]);
      reset();
      showToast("success", response?.responseDetails?.detail);
      setShowConfirmationDialog(false);
      recallEarningSummary(true);
      setSelectedTable("");
      setRecallEmployee(true);
      setUpdatedData([]);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const earningDetails =
    attendanceDetails?.earning_summary?.earning_summary_details ?? [];
  const otherEarningDetails =
    attendanceDetails?.earning_summary?.other_earnings ?? [];
  const deductionDetails = attendanceDetails?.earning_summary?.deduction ?? [];
  const otherDeductionDetails =
    attendanceDetails?.earning_summary?.other_deduction ?? [];

  const earningGrandTotal = earningDetails.reduce(
    (sum, item) => sum + (Number(item?.amount) || 0),
    0,
  );
  const otherEarningGrandTotal = otherEarningDetails.reduce(
    (sum, item) => sum + (Number(item?.total) || 0),
    0,
  );
  const deductionGrandTotal = deductionDetails.reduce(
    (sum, item) => sum + (Number(item?.amount) || 0),
    0,
  );
  const otherDeductionGrandTotal = otherDeductionDetails.reduce(
    (sum, item) => sum + (Number(item?.amount) || 0),
    0,
  );

  const usedComponentNames = new Set(
    updatedData
      ?.map((item) => (item.component || item.type)?.trim()?.toLowerCase())
      .filter(Boolean),
  );

  const RemoveOtherEarnings = async () => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
    const payload = {
      pay_schedule_id: payScheduleId,
      other_earning_id: deleteOtherEarning.id,
      user_id: userId,
    };
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/remove_other_earnings/?company_id=${requestCompanyId}`,
      "PATCH",
      payload,
    );
    if (response.ok) {
      setRecallEmployee(true);
      recallEarningSummary(true);
      showToast(
        "success",
        otherDeduction
          ? "Other deduction removed successfully"
          : "Other earning removed successfully",
      );
      setShowRemoveDialogBox(false);
    }
  };

  return loading ? (
    <Loader />
  ) : (
    <Grid container sx={{ p: 1, backgroundColor: themes?.primary }}>
      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCancelClose}
        onConfirm={handleSubmit(updateData)}
        title={"Adjustment"}
        confirmText={"Add"}
        cancelText="Cancel"
        showChildren={true}
      >
        <Grid container spacing={1} sx={{ py: 1, mb: 1 }}>
          <Grid item xs={3.5}></Grid>
          <Grid item xs={4}>
            <Typography color="text.secondary" display="flex" gap={2}>
              <span>Name</span>
              <strong>{attendanceDetails?.employee_name ?? "-"}</strong>
            </Typography>
          </Grid>

          <Grid item xs={4}>
            <Typography color="text.secondary" display="flex" gap={2}>
              <span>Type</span>
              <strong>{selectedTable}</strong>
            </Typography>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ width: 250, fontWeight: "bold" }}>
            Component Name
          </Typography>
          <CustomAutoComplete
            dataList={componentData
              ?.filter(
                (obj) =>
                  !usedComponentNames.has(obj?.name?.trim().toLowerCase()),
              )
              .map((obj) => ({
                ...obj,
                label: obj?.name,
              }))}
            rules={{
              ...register("component_name", {
                required: "Component name is required",
              }),
            }}
            selectedValue={watch("component_name")}
            updateDataFunction={hanldeComponetName}
            placeHolder="Select the component name"
            errorMessage={
              errors?.component_name?.message
                ? errors?.component_name?.message
                : ""
            }
            textBoxError={errors?.component_name ? true : false}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ width: 250, fontWeight: "bold" }}>
            Amount
          </Typography>

          <CustomTextBox
            rules={{
              ...register("amount", {
                required: "Amount is required",
              }),
            }}
            selectedValue={watch("amount")}
            updateDataFunction={hanldeAmount}
            placeHolder="Enter amount"
            errorMessage={
              errors?.amount?.message ? errors?.amount?.message : ""
            }
            textBoxError={errors?.amount ? true : false}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            sx={{
              width: 250,
              fontWeight: "bold",
            }}
          >
            Reason (Optional)
          </Typography>
          <CustomTextBox
            type="multiline"
            rules={{
              ...register("reason"),
            }}
            selectedValue={watch("reason")}
            updateDataFunction={handleReason}
            placeHolder="Enter a reason for adjustment"
          />
        </Box>
      </ConfirmationDialog>

      <SectionHeader>Earnings</SectionHeader>
      <DataTable
        data={attendanceDetails?.earning_summary?.earning_summary_details}
        columns={[<>Component</>, "Basis", "Qty", "Rate", "Total"]}
        renderRow={(data, index) => (
          <React.Fragment key={index}>
            <TableRow>
              <TableCell>{data?.type}</TableCell>
              <TableCell>{data?.based_on ?? "-"}</TableCell>
              <TableCell>{data?.quantity ?? "-"}</TableCell>
              <TableCell>₹{data?.rate}</TableCell>
              <TableCell>₹{data?.amount}</TableCell>
            </TableRow>
          </React.Fragment>
        )}
        summaryRow={() => (
          <>
            <TableRow>
              <TableCell>
                <strong>Grand Total</strong>
              </TableCell>
              <TableCell colSpan={3} />
              <TableCell>
                ₹{Number(earningGrandTotal ?? 0).toFixed(2)}
              </TableCell>
            </TableRow>
          </>
        )}
      />

      <SectionHeader>Other Earnings</SectionHeader>
      <DataTable
        data={attendanceDetails?.earning_summary?.other_earnings}
        columns={[
          <>Component</>,
          "Basis",
          "Qty",
          "Rate",
          "Factor",
          "Total",
          "Action",
        ]}
        renderRow={(data, index) => (
          <React.Fragment key={index}>
            <TableRow>
              <TableCell>{data?.component}</TableCell>
              <TableCell>{data?.basis ?? "-"}</TableCell>
              <TableCell>{data?.quantity ?? "-"}</TableCell>
              <TableCell>
                {data?.basis === "Amount"
                  ? "-"
                  : (data?.rate ?? data?.rate === 0)
                    ? `₹${data.rate}`
                    : "-"}
              </TableCell>
              <TableCell>
                {(data?.factor ?? data?.factor === 0) ? data.factor : "-"}
              </TableCell>
              <TableCell>₹{data?.total}</TableCell>
              <TableCell>
                {data?.is_manually_added && (
                  <IconButton
                    disabled={isEditable}
                    onClick={() => {
                      setDeleteOtherEarning(data);
                      setShowRemoveDialogBox(true);
                    }}
                  >
                    <Delete
                      color={isEditable ? themes.disabledColor : "error"}
                      sx={{ fontSize: 20, cursor: "pointer" }}
                    />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          </React.Fragment>
        )}
        emptyAction={() => (
          <IconButton
            disabled={isEditable}
            onClick={() => {
              setShowConfirmationDialog(true);
              setSelectedTable("Other Earnings");
              setTriggerApi(true);
              setUpdatedData(
                attendanceDetails?.earning_summary?.other_earnings,
              );
            }}
          >
            <AddCircleOutline sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        summaryRow={() => (
          <>
            <TableRow>
              <TableCell sx={{ cursor: "pointer" }}>
                <IconButton
                  disabled={isEditable}
                  onClick={() => {
                    setShowConfirmationDialog(true);
                    setSelectedTable("Other Earnings");
                    setUpdatedData(
                      attendanceDetails?.earning_summary?.other_earnings,
                    );
                    setTriggerApi(true);
                  }}
                >
                  <AddCircleOutline sx={{ fontSize: 20 }} />
                </IconButton>
              </TableCell>
              <TableCell colSpan={6} />
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Grand Total</strong>
              </TableCell>
              <TableCell colSpan={4} />
              <TableCell>
                <strong>
                  ₹{Number(otherEarningGrandTotal ?? 0).toFixed(2)}
                </strong>
              </TableCell>
            </TableRow>
          </>
        )}
      />

      <SectionHeader>Deductions</SectionHeader>
      <DataTable
        data={attendanceDetails?.earning_summary?.deduction}
        columns={[<>Component</>, "Basis", "Qty", "Rate", "Amount"]}
        renderRow={(data, index) => (
          <React.Fragment key={index}>
            <TableRow>
              <TableCell>{data?.type}</TableCell>
              <TableCell>{data?.based_on ?? "0"}</TableCell>
              <TableCell>{data?.quantity ?? "0"}</TableCell>
              <TableCell>₹{data?.rate}</TableCell>
              <TableCell>₹{data?.amount}</TableCell>
            </TableRow>
          </React.Fragment>
        )}
        summaryRow={() => (
          <>
            <TableRow>
              <TableCell>
                <strong>Grand Total</strong>
              </TableCell>
              <TableCell colSpan={3} />
              <TableCell>
                ₹{Number(deductionGrandTotal ?? 0).toFixed(2)}
              </TableCell>
            </TableRow>
          </>
        )}
      />

      <SectionHeader>Other Deductions</SectionHeader>
      <DataTable
        data={attendanceDetails?.earning_summary?.other_deduction}
        columns={["Component", "Basis", "Qty", "Rate", "Amount", "Action"]}
        renderRow={(data, index) => (
          <TableRow key={index}>
            <TableCell>{data?.type}</TableCell>
            <TableCell>{data?.based_on ?? "0"}</TableCell>
            <TableCell>{data?.quantity ?? "0"}</TableCell>
            <TableCell>₹{data?.rate}</TableCell>
            <TableCell>₹{data?.amount}</TableCell>
            <TableCell>
              {data?.is_manually_added && (
                <IconButton
                  disabled={isEditable}
                  onClick={() => {
                    setOtherDeduction(true);
                    setDeleteOtherEarning(data);
                    setShowRemoveDialogBox(true);
                  }}
                >
                  <Delete
                    color={isEditable ? themes.disabledColor : "error"}
                    sx={{ fontSize: 20, cursor: "pointer" }}
                  />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        )}
        emptyAction={() => (
          <IconButton
            disabled={isEditable}
            onClick={() => {
              setUpdatedData(
                attendanceDetails?.earning_summary?.other_deduction,
              );
              setShowConfirmationDialog(true);
              setSelectedTable("Other Deduction");
              setTriggerApi(true);
            }}
          >
            <AddCircleOutline sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        summaryRow={() => (
          <>
            <TableRow>
              <TableCell sx={{ cursor: "pointer" }}>
                <IconButton
                  disabled={isEditable}
                  onClick={() => {
                    setUpdatedData(
                      attendanceDetails?.earning_summary?.other_deduction,
                    );
                    setShowConfirmationDialog(true);
                    setSelectedTable("Other Deduction");
                    setTriggerApi(true);
                  }}
                >
                  <AddCircleOutline sx={{ fontSize: 20 }} />
                </IconButton>
              </TableCell>
              <TableCell colSpan={5} />
            </TableRow>

            <TableRow>
              <TableCell>
                <strong>Grand Total</strong>
              </TableCell>
              <TableCell colSpan={3} />
              <TableCell>
                ₹{Number(otherDeductionGrandTotal ?? 0).toFixed(2)}
              </TableCell>
            </TableRow>
          </>
        )}
      />

      <SectionHeader>Manual Adjustment History</SectionHeader>
      <Mui.TableContainer
        component={Mui.Paper}
        sx={{ mt: 2, width: "100%", maxHeight: 850 }}
      >
        <Mui.Table>
          <Mui.TableHead
            sx={{
              backgroundColor: themes.primary,
              position: "sticky",
              top: 0,
              zIndex: 10,
              "& .MuiTableCell-root": {
                height: "15px",
                padding: "10px 20px",
                color: themes.headLine,
                fontWeight: "bold",
              },
            }}
          >
            <Mui.TableRow>
              <Mui.TableCell>Adjusted by</Mui.TableCell>
              <Mui.TableCell>Adjusted on</Mui.TableCell>
              <Mui.TableCell>Adjustment</Mui.TableCell>
              <Mui.TableCell>Reason</Mui.TableCell>
            </Mui.TableRow>
          </Mui.TableHead>

          <Mui.TableBody>
            {attendanceDetails?.earning_summary?.manual_adjustment_history
              ?.length === 0 ? (
              <TableEmptyState
                dataLength={
                  attendanceDetails?.earning_summary?.manual_adjustment_history
                }
              />
            ) : (
              attendanceDetails?.earning_summary?.manual_adjustment_history?.map(
                (row, index) => (
                  <React.Fragment key={index}>
                    <Mui.TableRow>
                      <Mui.TableCell>
                        {row.adjusted_by ? row.adjusted_by : "N/A"}
                      </Mui.TableCell>
                      <Mui.TableCell>
                        {row.modifiedat ? row.modifiedat : "N/A"}
                      </Mui.TableCell>
                      <Mui.TableCell>
                        {row.adjustment ? row.adjustment : "N/A"}
                      </Mui.TableCell>
                      <Mui.TableCell>
                        {row.reason ? row.reason : "N/A"}
                      </Mui.TableCell>
                    </Mui.TableRow>
                  </React.Fragment>
                ),
              )
            )}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>
      <ConfirmationDialog
        open={showRemoveDialogBox}
        onClose={() => {
          setShowRemoveDialogBox(false);
          setOtherDeduction(false);
        }}
        onConfirm={RemoveOtherEarnings}
        title={
          otherDeduction ? "Remove Other Deductions" : "Remove Other Earnings"
        }
        confirmText={"Remove"}
        showChildren={true}
        cancelText="Cancel"
      >
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Are you sure you want to remove this{" "}
              {otherDeduction ? "Other Deductions" : "Other Earning"} ?
            </Typography>
          </Box>
        </Box>
      </ConfirmationDialog>
    </Grid>
  );
};
